import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Package, 
  Search,
  AlertCircle,
  Loader2,
  X,
  Eye,
  ArrowUpDown,
  Filter
} from 'lucide-react';
import axios from 'axios';
import { getSectionPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '-';
  return `₹${amount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const getPurchaseRateIncludingGst = (item) => {
  if (item.purchase_rate === null || item.purchase_rate === undefined || item.purchase_rate === '') return null;
  const purchaseRate = Number(item.purchase_rate);
  const taxPercentage = Number(item.tax_percentage || 0);
  return purchaseRate * (1 + taxPercentage / 100);
};

export const Inventory = () => {
  const navigate = useNavigate();
  const perms = getSectionPerms('inventory');
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Filters
  const [filterCompany, setFilterCompany] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterCategoryInput, setFilterCategoryInput] = useState('');
  const [showCatSuggest, setShowCatSuggest] = useState(false);
  const [filterPriceMin, setFilterPriceMin] = useState('');
  const [filterPriceMax, setFilterPriceMax] = useState('');
  const [sortOrder, setSortOrder] = useState('');

  // Applied filters (only update on Apply click)
  const [appliedFilters, setAppliedFilters] = useState({ company: '', category: '', priceMin: '', priceMax: '', sort: '' });

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchInventory();
  }, []);

  const fetchInventory = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${BACKEND_URL}/api/inventory`, getAuthHeaders());
      setInventory(res.data);
    } catch (err) {
      console.error('Error fetching inventory:', err);
      setError('Failed to fetch inventory');
    } finally {
      setLoading(false);
    }
  };

  const companyNames = [...new Set(inventory.map(i => i.company_name).filter(Boolean))].sort();
  const categoryNames = [...new Set(inventory.map(i => i.category_name).filter(Boolean))].sort();

  const applyFilters = () => {
    setAppliedFilters({ company: filterCompany, category: filterCategory, priceMin: filterPriceMin, priceMax: filterPriceMax, sort: sortOrder });
  };

  const catSuggestions = filterCategoryInput.trim()
    ? categoryNames.filter(n => n.toLowerCase().includes(filterCategoryInput.toLowerCase()))
    : categoryNames.slice(0, 8);

  const clearFilters = () => {
    setFilterCompany(''); setFilterCategory(''); setFilterCategoryInput(''); setFilterPriceMin(''); setFilterPriceMax(''); setSortOrder('');
    setAppliedFilters({ company: '', category: '', priceMin: '', priceMax: '', sort: '' });
  };

  const hasActiveFilters = appliedFilters.company || appliedFilters.category || appliedFilters.priceMin || appliedFilters.priceMax || appliedFilters.sort;

  const normalize = (str) => (str || '').toLowerCase().replace(/[\s\-\/\\.,:;_()+]+/g, '');
  const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const filteredInventory = (() => {
    let result = inventory.filter(item => {
      const terms = searchQuery.trim().split(/\s+/).filter(Boolean);
      if (!terms.length) return true;
      const haystack = [item.product_name, item.product_code, item.brand_name, item.category_name].map(normalize).join('');
      const pattern = new RegExp(terms.map(t => `(?=.*${escapeRegex(normalize(t))})`).join(''), 'i');
      return pattern.test(haystack);
    });
    if (appliedFilters.company) {
      result = result.filter(item => item.company_name === appliedFilters.company);
    }
    if (appliedFilters.category) {
      result = result.filter(item => item.category_name === appliedFilters.category);
    }
    if (appliedFilters.priceMin !== '') {
      const min = parseFloat(appliedFilters.priceMin);
      if (!isNaN(min)) result = result.filter(item => (item.display_rate || 0) >= min);
    }
    if (appliedFilters.priceMax !== '') {
      const max = parseFloat(appliedFilters.priceMax);
      if (!isNaN(max)) result = result.filter(item => (item.display_rate || 0) <= max);
    }
    if (appliedFilters.sort === 'low-high') {
      result = [...result].sort((a, b) => (a.display_rate || 0) - (b.display_rate || 0));
    } else if (appliedFilters.sort === 'high-low') {
      result = [...result].sort((a, b) => (b.display_rate || 0) - (a.display_rate || 0));
    }
    return result;
  })();

  return (
    <div data-testid="inventory-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Inventory</h1>
          <p className="text-slate-600 mt-1">View stock levels and pricing by product</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="bg-orange-100 text-orange-700 px-4 py-2 rounded-sm">
            <span className="font-bold">{inventory.length}</span> Products in Stock
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search by product name, code, brand or category..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none transition-colors"
          />
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="h-4 w-4 text-slate-500" />
          <span className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Filters</span>
          {hasActiveFilters && (
            <button onClick={clearFilters} data-testid="clear-filters-btn"
              className="ml-auto text-xs text-red-500 hover:text-red-600 font-medium">Clear All</button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
          {/* Company */}
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Company</label>
            <select value={filterCompany} onChange={e => setFilterCompany(e.target.value)} data-testid="filter-company"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none bg-white">
              <option value="">All Companies</option>
              {companyNames.map(name => <option key={name} value={name}>{name}</option>)}
            </select>
          </div>
          {/* Category autocomplete */}
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Category</label>
            <div className="relative">
              <input
                type="text"
                value={filterCategoryInput}
                onChange={e => { setFilterCategoryInput(e.target.value); setFilterCategory(''); setShowCatSuggest(true); }}
                onFocus={() => setShowCatSuggest(true)}
                onBlur={() => setTimeout(() => setShowCatSuggest(false), 150)}
                placeholder="Type category..."
                data-testid="filter-category-input"
                className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none"
              />
              {filterCategoryInput && (
                <button onClick={() => { setFilterCategory(''); setFilterCategoryInput(''); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X className="h-4 w-4" />
                </button>
              )}
              {showCatSuggest && catSuggestions.length > 0 && (
                <div className="absolute z-20 top-full left-0 right-0 mt-0.5 bg-white border border-slate-200 rounded-sm shadow-lg max-h-44 overflow-auto">
                  {catSuggestions.map(name => (
                    <button key={name} onMouseDown={() => { setFilterCategory(name); setFilterCategoryInput(name); setShowCatSuggest(false); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-orange-50 hover:text-orange-700 ${filterCategory === name ? 'bg-orange-50 text-orange-700 font-medium' : 'text-slate-700'}`}>
                      {name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {/* Starting Price */}
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Min Display Price</label>
            <input type="number" value={filterPriceMin} onChange={e => setFilterPriceMin(e.target.value)}
              placeholder="₹ Min" data-testid="filter-price-min" min="0"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
          </div>
          {/* Ending Price */}
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Max Display Price</label>
            <input type="number" value={filterPriceMax} onChange={e => setFilterPriceMax(e.target.value)}
              placeholder="₹ Max" data-testid="filter-price-max" min="0"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none" />
          </div>
          {/* Sort */}
          <div>
            <label className="block text-xs font-medium text-slate-500 uppercase mb-1">Sort by Price</label>
            <select value={sortOrder} onChange={e => setSortOrder(e.target.value)} data-testid="filter-sort"
              className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm focus:border-orange-500 focus:outline-none bg-white">
              <option value="">Default</option>
              <option value="low-high">Low to High</option>
              <option value="high-low">High to Low</option>
            </select>
          </div>
          {/* Apply */}
          <div>
            <button onClick={applyFilters} data-testid="apply-filters-btn"
              className="w-full px-4 py-2 bg-orange-500 text-white text-sm font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors">
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Inventory Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16 bg-white rounded-sm border border-slate-200">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      ) : filteredInventory.length === 0 ? (
        <div className="bg-white rounded-sm border border-slate-200 text-center py-16" data-testid="empty-state">
          <Package className="h-16 w-16 mx-auto mb-4 text-slate-300" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No inventory data</h3>
          <p className="text-slate-500 mb-6">Add inward entries to see inventory here</p>
        </div>
      ) : (
        <div className="bg-white rounded-sm border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wider">
                <th className="px-4 py-3 text-left">Product</th>
                <th className="px-4 py-3 text-left">Category</th>
                <th className="px-4 py-3 text-left">Company</th>
                <th className="px-4 py-3 text-center">Stock</th>
                <th className="px-4 py-3 text-center">Tax</th>
                {!perms.hidePrices && <th className="px-4 py-3 text-right">Purchase (Incl. GST)</th>}
                {!perms.hidePrices && <th className="px-4 py-3 text-right">Sale</th>}
                <th className="px-4 py-3 text-right">Display Price</th>
                <th className="px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredInventory.map((item) => (
                <tr key={item.id} data-testid={`inventory-row-${item.id}`}
                  className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {item.product_image ? (
                        <img src={item.product_image} alt="" className="h-9 w-9 rounded object-cover border border-slate-200" />
                      ) : (
                        <div className="h-9 w-9 rounded bg-slate-100 flex items-center justify-center"><Package className="h-4 w-4 text-slate-300" /></div>
                      )}
                      <span className="font-medium text-slate-900">{item.product_name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">{item.category_name ? <span className="px-2 py-0.5 bg-purple-50 text-purple-700 text-xs rounded">{item.category_name}</span> : '-'}</td>
                  <td className="px-4 py-3">{item.company_name ? <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded">{item.company_name}</span> : '-'}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${
                      item.quantity > 10 ? 'bg-green-100 text-green-700' : item.quantity > 0 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                    }`}>{item.quantity} {item.unit_name || 'units'}</span>
                  </td>
                  <td className="px-4 py-3 text-center text-xs text-slate-600">{item.tax_percentage ? `${item.tax_percentage}%` : '-'}</td>
                  {!perms.hidePrices && (
                    <td className="px-4 py-3 text-right text-slate-600" data-testid={`purchase-rate-including-gst-${item.id}`}>
                      {formatCurrency(getPurchaseRateIncludingGst(item))}
                    </td>
                  )}
                  {!perms.hidePrices && <td className="px-4 py-3 text-right text-slate-600">₹{item.sale_rate?.toLocaleString() || '-'}</td>}
                  <td className="px-4 py-3 text-right font-bold text-orange-600">₹{item.display_rate?.toLocaleString() || '-'}</td>
                  <td className="px-4 py-3 text-center">
                    <button onClick={() => navigate(`/admin/inventory/${item.product_id}`)} data-testid={`view-btn-${item.id}`}
                      className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors" title="View Details">
                      <Eye className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
